/**
 * Boards demo store: frontend-only data behind the boards UI; no backend.
 * Reseeded on every page load, never persisted.
 */
import {newUuid} from '@u/uuid';
// Type only: the auth store imports this store, so importing it back would be a cycle.
import type {IAuthUser} from '@shared/auth_types';
import {defineStore} from 'pinia';
import type {IBoardsSeedData} from './board_seed';
import {BOARD_SWATCH_COLORS} from '@s/app_colors';
import {createSeedData} from './board_seed';
import type {
   IApiKey,
   IBoard,
   IBoardShareEntry,
   ICard,
   ICardViewState,
   IFolderTreeNode,
   IInvitation,
   ILabel,
   IMember,
   IOrganization,
} from './board_demo_data_types';
import {
   ApiKeyOwnerType,
   BoardAccess,
   CardGroupBy,
   CardPriority,
   CardSortBy,
   CardStatus,
   FolderNodeType,
   InvitationStatus,
   MemberStatus,
   OrgRole,
   SeatType,
} from './board_demo_data_types';
import type {ICardGroup} from './board_view_utils';
import {applyCardView, countActiveFilters, filterCards, groupCards} from './board_view_utils';

export interface IBoardsState extends IBoardsSeedData {
   view: ICardViewState;

   /** Whether the docked board-settings panel is open (session-only UI state). */
   isBoardSettingsOpen: boolean;

   /** Active organization id (from the real session) that boards are scoped
    * to; null until ensureWorkspaceContext() runs. */
   activeOrganizationId: string | null;
}

/** Flattened sidebar tree row (folder or board) ready for rendering. */
export interface ITreeRow {
   key: string;
   type: FolderNodeType;
   id: string;
   name: string;
   /** Board swatch color; null for folders. */
   color: string | null;
   depth: number;
   /** Contained-board count for folders; empty for boards. */
   countLabel: string;
   isActive: boolean;
   isOpen: boolean;
}

/** Board share entry resolved with its member for display. */
export interface IShareRow extends IBoardShareEntry {
   member: IMember;
}

function createInitialState(): IBoardsState {
   return {
      ...createSeedData(),
      view: {
         query: '',
         filterPriority: null,
         filterLabelId: null,
         filterAssigneeId: null,
         sortBy: CardSortBy.MANUAL,
         groupBy: CardGroupBy.STATUS,
      },
      isBoardSettingsOpen: false,
      activeOrganizationId: null,
   };
}

export const useDemoBoardsStore = defineStore('demoBoards', {
   state: (): IBoardsState => createInitialState(),

   getters: {
      /** Boards of the active organization; empty until one is set. */
      visibleBoards(state): IBoard[] {
         if (state.activeOrganizationId == null) {
            return [];
         }
         return state.boards.filter(
            (b) => (b.organizationId ?? null) === state.activeOrganizationId,
         );
      },

      currentBoard(state): IBoard {
         const visible = this.visibleBoards;
         return (
            visible.find((b) => b.id === state.currentBoardId) ??
            visible[0] ??
            // Before an organization is set, fall back to any board
            state.boards.find((b) => b.id === state.currentBoardId) ??
            state.boards[0]
         );
      },

      currentUser(state): IMember {
         return state.members.find((m) => m.id === state.currentUserId) ?? state.members[0];
      },

      boardById(state): (board_id: string) => IBoard | null {
         return (board_id) => state.boards.find((b) => b.id === board_id) ?? null;
      },

      memberById(state): (member_id: string | null) => IMember | null {
         return (member_id) =>
            member_id == null ? null : (state.members.find((m) => m.id === member_id) ?? null);
      },

      /** Label lookup within the current board. */
      labelById(): (label_id: string | null) => ILabel | null {
         return (label_id) =>
            label_id == null
               ? null
               : (this.currentBoard.labels.find((l) => l.id === label_id) ?? null);
      },

      /** Find a card on any board; null when not found. */
      cardById(state): (card_id: string) => ICard | null {
         return (card_id) => {
            for (const board of state.boards) {
               const card = board.cards.find((c) => c.id === card_id);
               if (card != null) {
                  return card;
               }
            }
            return null;
         };
      },

      /** Members of the current board, in board order. */
      boardMembers(): IMember[] {
         return this.currentBoard.memberIds
            .map((id) => this.memberById(id))
            .filter((m): m is IMember => m != null);
      },

      /** Current board's cards with query/filters/sort applied. */
      visibleCards(state): ICard[] {
         return applyCardView(this.currentBoard.cards, state.view);
      },

      /** Visible cards grouped per the view's group-by mode. */
      groupedColumns(state): ICardGroup[] {
         return groupCards(this.visibleCards, state.view.groupBy, this.currentBoard.memberIds);
      },

      activeFilterCount(state): number {
         return countActiveFilters(state.view);
      },

      /** Cards on the current board matching a text query, ignoring the view's filters. */
      searchCards(): (query: string) => ICard[] {
         return (query) =>
            filterCards(this.currentBoard.cards, {
               query,
               filterPriority: null,
               filterLabelId: null,
               filterAssigneeId: null,
               sortBy: CardSortBy.MANUAL,
               groupBy: CardGroupBy.STATUS,
            });
      },

      /** Current board's share entries resolved with member records. */
      boardShareRows(): IShareRow[] {
         return this.currentBoard.share
            .map((entry) => {
               const member = this.memberById(entry.memberId);
               return member == null ? null : {...entry, member};
            })
            .filter((row): row is IShareRow => row != null);
      },

      unreadNotificationCount(state): number {
         return state.notifications.filter((n) => !n.isRead).length;
      },

      /**
       * Sidebar tree flattened to renderable rows. `filter_text` narrows to
       * matching boards (folders with matches are forced open).
       */
      visibleTreeRows(state): (filter_text: string) => ITreeRow[] {
         return (filter_text) => {
            const query = filter_text.trim().toLowerCase();
            const rows: ITreeRow[] = [];

            // Only the active organization's boards appear in the tree
            const visible_ids = new Set(this.visibleBoards.map((b) => b.id));

            const board_matches = (board_id: string): boolean => {
               const board = this.boardById(board_id);
               return (
                  board != null &&
                  visible_ids.has(board_id) &&
                  board.name.toLowerCase().includes(query)
               );
            };

            const count_boards = (node: IFolderTreeNode): number => {
               if (node.type === FolderNodeType.BOARD) {
                  return visible_ids.has(node.id) ? 1 : 0;
               }
               return (node.children ?? []).reduce((sum, child) => sum + count_boards(child), 0);
            };

            const has_match = (node: IFolderTreeNode): boolean => {
               if (node.type === FolderNodeType.BOARD) {
                  return board_matches(node.id);
               }
               return (node.children ?? []).some((child) => has_match(child));
            };

            const walk = (node: IFolderTreeNode, depth: number): void => {
               if (query.length > 0 && !has_match(node)) {
                  return;
               }

               if (node.type === FolderNodeType.BOARD) {
                  const board = this.boardById(node.id);
                  if (board == null || !visible_ids.has(board.id)) {
                     return;
                  }
                  rows.push({
                     key: `b-${board.id}`,
                     type: FolderNodeType.BOARD,
                     id: board.id,
                     name: board.name,
                     color: board.color,
                     depth,
                     countLabel: '',
                     isActive: board.id === state.currentBoardId,
                     isOpen: false,
                  });
                  return;
               }

               // Folders with no boards in the active org stay hidden
               if (count_boards(node) === 0) {
                  return;
               }

               const is_open = query.length > 0 || state.openFolderIds.includes(node.id);
               rows.push({
                  key: `f-${node.id}`,
                  type: FolderNodeType.FOLDER,
                  id: node.id,
                  name: node.name ?? '',
                  color: null,
                  depth,
                  countLabel: String(count_boards(node)),
                  isActive: false,
                  isOpen: is_open,
               });
               if (is_open) {
                  for (const child of node.children ?? []) {
                     walk(child, depth + 1);
                  }
               }
            };

            for (const node of state.folderTree) {
               walk(node, 0);
            }
            return rows;
         };
      },

      // ----- invitations and API keys -----

      /** Pending invitations addressed to the current user (matched by email). */
      myPendingInvitations(): IInvitation[] {
         const email = this.currentUser.email.toLowerCase();
         return this.invitations.filter(
            (i) => i.email.toLowerCase() === email && i.status === InvitationStatus.PENDING,
         );
      },

      /** API keys owned by the current user. */
      userApiKeys(state): IApiKey[] {
         return state.apiKeys.filter(
            (k) => k.ownerType === ApiKeyOwnerType.USER && k.ownerId === state.currentUserId,
         );
      },

      /** API keys owned by the active organization. */
      orgApiKeys(state): IApiKey[] {
         return state.apiKeys.filter(
            (k) =>
               k.ownerType === ApiKeyOwnerType.ORGANIZATION &&
               k.ownerId === state.organization.id,
         );
      },

   },

   actions: {
      // ----- boards / tree -----

      selectBoard(board_id: string): void {
         if (this.boards.some((b) => b.id === board_id)) {
            this.currentBoardId = board_id;
         }
      },

      toggleBoardSettings(): void {
         this.isBoardSettingsOpen = !this.isBoardSettingsOpen;
      },

      /** Switch the active organization (demo: swaps the org record only). */
      toggleFolder(folder_id: string): void {
         if (this.openFolderIds.includes(folder_id)) {
            this.openFolderIds = this.openFolderIds.filter((id) => id !== folder_id);
         } else {
            this.openFolderIds.push(folder_id);
         }
      },

      toggleStar(board_id: string): void {
         const board = this.boards.find((b) => b.id === board_id);
         if (board != null) {
            board.isStarred = !board.isStarred;
         }
      },

      /**
       * Point the demo data at the signed-in user and active organization: the
       * user becomes the current member and boards are scoped to the
       * organization. The first organization seen takes over the seed boards;
       * any other organization gets one starter board. Safe to call repeatedly.
       */
      ensureWorkspaceContext(opts: {
         orgId: string | null;
         orgRole: string | null;
         user: IAuthUser | null;
      }): void {
         this.syncRealUser(opts.user, opts.orgRole);
         this.activeOrganizationId = opts.orgId;
         if (opts.orgId == null) {
            return;
         }

         const is_adopted = this.boards.some((b) => b.organizationId != null);
         if (!is_adopted) {
            for (const board of this.boards) {
               board.organizationId = opts.orgId;
            }
         }

         // The boards UI assumes a current board, so every organization gets one
         if (!this.boards.some((b) => b.organizationId === opts.orgId)) {
            this.addBoard({name: 'Welcome board'});
         }

         // The selected board must belong to the active organization
         if (this.currentBoard.organizationId !== opts.orgId) {
            const first_visible = this.boards.find((b) => b.organizationId === opts.orgId);
            if (first_visible != null) {
               this.currentBoardId = first_visible.id;
            }
         }
      },

      /** Add or update the signed-in user as a demo member and make them current; no-op when null. */
      syncRealUser(user: IAuthUser | null, org_role: string | null): void {
         if (user == null) {
            return;
         }

         const role = Object.values(OrgRole).find((r) => r === org_role) ?? OrgRole.MEMBER;
         const existing = this.members.find((m) => m.id === user.id);
         if (existing != null) {
            existing.name = user.name;
            existing.email = user.email;
            existing.emailVerified = user.emailVerified;
            existing.username = user.username ?? '';
            existing.displayUsername = user.displayUsername ?? user.name;
            existing.initials = buildInitials(user.name);
            existing.orgRole = role;
         } else {
            this.members.push({
               id: user.id,
               name: user.name,
               email: user.email,
               emailVerified: user.emailVerified,
               username: user.username ?? '',
               displayUsername: user.displayUsername ?? user.name,
               initials: buildInitials(user.name),
               color: BOARD_SWATCH_COLORS[this.members.length % BOARD_SWATCH_COLORS.length],
               orgRole: role,
               status: MemberStatus.ACTIVE,
               isAppAdmin: false,
               isBanned: false,
               banReason: null,
               banExpiresLabel: null,
               title: '',
               department: '',
               seat: SeatType.FULL,
               twoFactorEnabled: false,
               joinedLabel: 'today',
               lastActiveLabel: 'now',
            });
         }
         this.currentUserId = user.id;
      },

      /** Create a board (optionally inside a folder), select it, return its id. */
      addBoard(opts: {name?: string; folderId?: string} = {}): string {
         const board_id = newUuid();

         this.boards.push({
            id: board_id,
            organizationId: this.activeOrganizationId,
            name: opts.name ?? `New board ${this.boards.length + 1}`,
            color: BOARD_SWATCH_COLORS[this.boards.length % BOARD_SWATCH_COLORS.length],
            isStarred: false,
            description: '',
            visibility: this.currentBoard.visibility,
            memberIds: [this.currentUserId],
            labels: [],
            fields: [],
            cards: [],
            share: [{memberId: this.currentUserId, access: BoardAccess.EDIT}],
         });

         const new_node: IFolderTreeNode = {type: FolderNodeType.BOARD, id: board_id};
         const folder = opts.folderId != null ? findFolder(this.folderTree, opts.folderId) : null;
         if (folder != null) {
            (folder.children ??= []).push(new_node);
         } else {
            this.folderTree.push(new_node);
         }

         this.currentBoardId = board_id;
         return board_id;
      },

      // ----- cards -----

      /** Add a card to the current board and return its id. */
      addCard(status: CardStatus = CardStatus.TODO): string {
         const card_id = newUuid();
         this.currentBoard.cards.push({
            id: card_id,
            title: 'New card',
            status,
            priority: CardPriority.MEDIUM,
            assigneeId: null,
            dueDate: null,
            description: '',
            labelIds: [],
            fieldValues: {},
            createdDate: null,
            watcherIds: [],
            checklist: [],
            comments: [],
            activity: [],
         });
         return card_id;
      },

      updateCard(card_id: string, patch: Partial<ICard>): void {
         const card = this.cardById(card_id);
         if (card != null) {
            Object.assign(card, patch);
         }
      },

      moveCard(card_id: string, status: CardStatus): void {
         this.updateCard(card_id, {status});
      },

      removeCard(card_id: string): void {
         for (const board of this.boards) {
            const idx = board.cards.findIndex((c) => c.id === card_id);
            if (idx !== -1) {
               board.cards.splice(idx, 1);
               return;
            }
         }
      },

      toggleChecklistItem(card_id: string, item_index: number): void {
         const item = this.cardById(card_id)?.checklist[item_index];
         if (item != null) {
            item.isDone = !item.isDone;
         }
      },

      addChecklistItem(card_id: string, text: string): void {
         const card = this.cardById(card_id);
         if (card != null && text.trim().length > 0) {
            card.checklist.push({text: text.trim(), isDone: false});
         }
      },

      addComment(card_id: string, text: string): void {
         const card = this.cardById(card_id);
         if (card != null && text.trim().length > 0) {
            card.comments.push({
               memberId: this.currentUserId,
               text: text.trim(),
               whenLabel: 'just now',
            });
         }
      },

      // ----- board members / labels -----

      /**
       * Add a member to the current board by name or email — matches an
       * existing org member first, otherwise creates a pending one.
       */
      addBoardMember(name_or_email: string): void {
         const value = name_or_email.trim();
         if (value.length === 0) {
            return;
         }

         const lower = value.toLowerCase();
         let member = this.members.find(
            (m) => m.email.toLowerCase() === lower || m.name.toLowerCase() === lower,
         );
         member ??= this.createPendingMember(value);

         const board = this.currentBoard;
         if (!board.share.some((s) => s.memberId === member.id)) {
            board.share.push({memberId: member.id, access: BoardAccess.EDIT});
         }
         if (!board.memberIds.includes(member.id)) {
            board.memberIds.push(member.id);
         }
      },

      /** Create a pending org member from a display name or email address. */
      createPendingMember(name_or_email: string): IMember {
         const is_email = name_or_email.includes('@') && !name_or_email.includes(' ');
         const name = is_email ? name_or_email.split('@')[0] : name_or_email;
         const email = is_email
            ? name_or_email
            : `${name_or_email.toLowerCase().replaceAll(/\s+/g, '.')}@${this.organization.domain}`;

         const username = buildUniqueUsername(name, this.members);
         const member: IMember = {
            id: newUuid(),
            name,
            email,
            emailVerified: false,
            username,
            displayUsername: username,
            initials: buildInitials(name),
            color: BOARD_SWATCH_COLORS[this.members.length % BOARD_SWATCH_COLORS.length],
            orgRole: this.organization.defaultRole,
            status: MemberStatus.PENDING,
            isAppAdmin: false,
            isBanned: false,
            banReason: null,
            banExpiresLabel: null,
            title: '',
            department: 'Product',
            seat: this.currentUser.seat,
            twoFactorEnabled: false,
            joinedLabel: 'Pending',
            lastActiveLabel: '—',
         };
         this.members.push(member);
         return member;
      },

      removeBoardMember(member_id: string): void {
         const board = this.currentBoard;
         board.share = board.share.filter((s) => s.memberId !== member_id);
         board.memberIds = board.memberIds.filter((id) => id !== member_id);
      },

      setBoardAccess(member_id: string, access: BoardAccess): void {
         const entry = this.currentBoard.share.find((s) => s.memberId === member_id);
         if (entry != null) {
            entry.access = access;
         }
      },

      addLabel(name?: string): void {
         const board = this.currentBoard;
         board.labels.push({
            id: newUuid(),
            name: name ?? `New label ${board.labels.length + 1}`,
            color: BOARD_SWATCH_COLORS[board.labels.length % BOARD_SWATCH_COLORS.length],
         });
      },

      // ----- view state -----

      setQuery(query: string): void {
         this.view.query = query;
      },

      toggleFilterPriority(priority: CardPriority): void {
         this.view.filterPriority = this.view.filterPriority === priority ? null : priority;
      },

      toggleFilterLabel(label_id: string): void {
         this.view.filterLabelId = this.view.filterLabelId === label_id ? null : label_id;
      },

      toggleFilterAssignee(member_id: string): void {
         this.view.filterAssigneeId = this.view.filterAssigneeId === member_id ? null : member_id;
      },

      setSortBy(sort_by: CardSortBy): void {
         this.view.sortBy = sort_by;
      },

      setGroupBy(group_by: CardGroupBy): void {
         this.view.groupBy = group_by;
      },

      clearFilters(): void {
         this.view.query = '';
         this.view.filterPriority = null;
         this.view.filterLabelId = null;
         this.view.filterAssigneeId = null;
      },

      // ----- org / user / notifications -----

      markNotificationsRead(): void {
         for (const notification of this.notifications) {
            notification.isRead = true;
         }
      },

      // ----- API keys -----

      /**
       * Create an API key and return its record with the full secret. The store
       * keeps only `start`; the secret is not retrievable later.
       */
      createApiKey(opts: {
         ownerType: ApiKeyOwnerType;
         name: string;
         expiresLabel: string | null;
      }): {record: IApiKey; secret: string} {
         const secret = generateApiSecret();
         const record: IApiKey = {
            id: newUuid(),
            ownerType: opts.ownerType,
            ownerId:
               opts.ownerType === ApiKeyOwnerType.USER ? this.currentUserId : this.organization.id,
            name: opts.name.trim().length > 0 ? opts.name.trim() : 'Untitled key',
            start: secret.slice(0, 12),
            createdLabel: 'Just now',
            expiresLabel: opts.expiresLabel,
            enabled: true,
            lastRequestLabel: null,
            requestCount: 0,
         };
         this.apiKeys.push(record);
         return {record, secret};
      },

      updateApiKey(key_id: string, patch: Partial<Pick<IApiKey, 'name' | 'enabled'>>): void {
         const key = this.apiKeys.find((k) => k.id === key_id);
         if (key != null) {
            Object.assign(key, patch);
         }
      },

      revokeApiKey(key_id: string): void {
         this.apiKeys = this.apiKeys.filter((k) => k.id !== key_id);
      },
   },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Two-letter initials from a display name or email-derived name. */
function buildInitials(name: string): string {
   const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
   if (parts.length === 0) {
      return '??';
   }
   if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
   }
   return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Unique normalized username from a display name (first token + numeric suffix). */
function buildUniqueUsername(name: string, members: readonly IMember[]): string {
   const base = (name.trim().split(/\s+/)[0] ?? 'user').toLowerCase().replaceAll(/[^a-z0-9]/g, '');
   const root = base.length > 0 ? base : 'user';

   let candidate = root;
   let suffix = 2;
   while (members.some((m) => m.username === candidate)) {
      candidate = `${root}${suffix}`;
      suffix += 1;
   }
   return candidate;
}

/** Random full API key secret ("ab_live_…"); shown once at creation only. */
function generateApiSecret(): string {
   const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
   const random_part = Array.from(
      crypto.getRandomValues(new Uint8Array(28)),
      (byte) => alphabet[byte % alphabet.length],
   ).join('');
   return `ab_live_${random_part}`;
}

/** Depth-first search for a folder node by id. */
function findFolder(nodes: readonly IFolderTreeNode[], folder_id: string): IFolderTreeNode | null {
   for (const node of nodes) {
      if (node.type !== FolderNodeType.FOLDER) {
         continue;
      }
      if (node.id === folder_id) {
         return node;
      }
      const found = findFolder(node.children ?? [], folder_id);
      if (found != null) {
         return found;
      }
   }
   return null;
}
